import { collectPages } from "../../shared/api/collect-pages.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { requireData } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import type { components } from "../../shared/api/generated/trading-engine.js";
import { backtestView } from "./backtest-models.js";
import { botKeys, settleOperation } from "./queries.js";
import type { BacktestStatus } from "./backtest-models.js";

type BacktestRequest = components["schemas"]["BacktestRequest"];

export const backtestKeys = {
  all: ["backtests"] as const,
  detail: (id: string) => ["backtests", id] as const,
  forBot: (botId: string) => ["backtests", "bot", botId] as const,
  list: (filters: BacktestListFilters, cursor: string, limit: number) =>
    ["backtests", "list", filters, cursor, limit] as const,
  summary: (filters: BacktestListFilters) => ["backtests", "summary", filters] as const,
  pnl: (filters: BacktestListFilters, groupBy: BacktestPnlGroupBy, range: BacktestPnlRange) =>
    ["backtests", "pnl", filters, groupBy, range] as const,
};

export type BacktestPnlGroupBy = "date" | "week" | "month" | "symbol";

export interface BacktestPnlRange {
  readonly pnlFrom?: string;
  readonly pnlTo?: string;
}

export interface BacktestListFilters {
  readonly botId?: string;
  readonly from?: string;
  readonly status?: BacktestStatus;
  readonly symbol?: string;
  readonly to?: string;
}

export function useBacktestPage(
  filters: BacktestListFilters,
  _cursor: string,
  _limit: number,
  enabled = true,
) {
  const api = useApi();
  return useQuery({
    enabled,
    queryKey: backtestKeys.list(filters, "", 100),
    queryFn: async ({ signal }) => {
      const page = await collectPages(async (cursor) =>
        requireData(
          await api.GET("/backtests", {
            params: {
              query: {
                ...filters,
                ...(cursor === "" ? {} : { cursor }),
                limit: 100,
              },
            },
            signal,
          }),
        ),
      );
      return { ...page, items: page.items.map(backtestView) };
    },
    refetchInterval: (query) =>
      query.state.data?.items.some((run) => run.status === "queued" || run.status === "running") ===
      true
        ? 2_000
        : false,
  });
}

export function useBacktestPnlChart(
  filters: BacktestListFilters,
  groupBy: BacktestPnlGroupBy,
  range: BacktestPnlRange,
  enabled = true,
) {
  const api = useApi();
  return useQuery({
    enabled,
    queryKey: backtestKeys.pnl(filters, groupBy, range),
    queryFn: async ({ signal }) =>
      requireData(
        await api.GET("/backtests/pnl", {
          params: { query: { ...filters, ...range, groupBy } },
          signal,
        }),
      ),
  });
}

export function useBacktestListSummary(filters: BacktestListFilters) {
  const api = useApi();
  return useQuery({
    queryKey: backtestKeys.summary(filters),
    queryFn: async ({ signal }) =>
      requireData(
        await api.GET("/backtests/summary", {
          params: { query: filters },
          signal,
        }),
      ),
    refetchInterval: (query) => {
      const value = query.state.data;
      return value !== undefined && value.queuedRuns + value.runningRuns > 0 ? 5_000 : false;
    },
  });
}

export function useBacktests(botId: string) {
  const api = useApi();
  return useQuery({
    queryKey: backtestKeys.forBot(botId),
    queryFn: async ({ signal }) => {
      const page = requireData(
        await api.GET("/backtests", { params: { query: { botId, limit: 100 } }, signal }),
      );
      return page.items
        .map(backtestView)
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    },
  });
}

export function useSymbolBacktests(symbol: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...backtestKeys.all, "symbol", symbol],
    queryFn: async ({ signal }) => {
      const page = requireData(
        await api.GET("/backtests", { params: { query: { limit: 100, symbol } }, signal }),
      );
      return page.items
        .map(backtestView)
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    },
  });
}

export function useBacktest(backtestId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    enabled: enabled && backtestId !== "",
    queryKey: backtestKeys.detail(backtestId),
    queryFn: async ({ signal }) =>
      backtestView(
        requireData(
          await api.GET("/backtests/{backtestId}", {
            params: { path: { backtestId } },
            signal,
          }),
        ),
      ),
    refetchInterval: (query) => {
      const value = query.state.data;
      return value?.status === "queued" || value?.status === "running" ? 2_000 : false;
    },
  });
}

export function useLaunchBacktest(botId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: BacktestRequest) => {
      const accepted = requireData(
        await api.POST("/bots/{botId}/backtests", {
          body,
          params: {
            header: { "x-idempotency-key": crypto.randomUUID() },
            path: { botId },
          },
        }),
      );
      const operation = await settleOperation(api, accepted.operation);
      return backtestView(
        (accepted.result ??
          operation.result ?? { id: operation.id }) as components["schemas"]["Resource"],
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: backtestKeys.forBot(botId) }),
        queryClient.invalidateQueries({ queryKey: botKeys.detail(botId) }),
      ]);
    },
  });
}

export function useCancelBacktest(backtestId: string, botId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const accepted = requireData(
        await api.POST("/backtests/{backtestId}/cancel", {
          params: {
            header: { "x-idempotency-key": crypto.randomUUID() },
            path: { backtestId },
          },
        }),
      );
      return settleOperation(api, accepted.operation);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: backtestKeys.detail(backtestId) }),
        queryClient.invalidateQueries({ queryKey: backtestKeys.forBot(botId) }),
      ]);
    },
  });
}

export function useDeleteBacktest(botId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (backtestId: string) =>
      requireData(
        await api.DELETE("/backtests/{backtestId}", {
          params: { path: { backtestId } },
        }),
      ),
    onSuccess: async (_result, backtestId) => {
      queryClient.removeQueries({ queryKey: backtestKeys.detail(backtestId) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: backtestKeys.all }),
        queryClient.invalidateQueries({ queryKey: botKeys.detail(botId) }),
      ]);
    },
  });
}
